import { useState } from "preact/hooks"
import { AlertTriangle, CheckCircle, XCircle, Loader2 } from "lucide-react"
import { confirmAction, ConfirmationAction } from "@/api/wizard"
import { Button } from "@/components/ui/button"

interface ConfirmationCardProps {
  action: ConfirmationAction
  conversationId: string
  className?: string
}

export function ConfirmationCard({ action, conversationId, className }: ConfirmationCardProps) {
  const [status, setStatus] = useState<"pending" | "confirming" | "confirmed" | "rejected">("pending")
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setStatus("confirming")
    setError(null)
    try {
      await confirmAction(conversationId, action.action_type, action.params)
      setStatus("confirmed")
    } catch (err: any) {
      setError(err.message || "Failed to execute action")
      setStatus("pending")
    }
  }

  function handleReject() {
    setStatus("rejected")
  }

  if (status === "confirmed") {
    return (
      <div class={`flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700 ${className || ""}`}>
        <CheckCircle className="w-4 h-4 flex-shrink-0" />
        <span>Action completed successfully.</span>
      </div>
    )
  }

  if (status === "rejected") {
    return (
      <div class={`flex items-center gap-2 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-sm text-gray-500 ${className || ""}`}>
        <XCircle className="w-4 h-4 flex-shrink-0" />
        <span>Action cancelled.</span>
      </div>
    )
  }

  return (
    <div class={`border border-amber-200 bg-amber-50 rounded-xl p-4 ${className || ""}`}>
      <div class="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div class="flex-1">
          <p class="text-sm font-medium text-gray-800 mb-1">Confirmation Required</p>
          <p class="text-sm text-gray-600 mb-3">{action.label}</p>

          {error && (
            <p class="text-sm text-red-600 mb-3">{error}</p>
          )}

          <div class="flex gap-2">
            <Button
              onClick={handleConfirm}
              disabled={status === "confirming"}
              variant="default"
              // @ts-ignore
              size="sm"
            >
              {status === "confirming" ? (
                <span class="flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Executing...
                </span>
              ) : (
                "Confirm"
              )}
            </Button>
            <Button
              onClick={handleReject}
              disabled={status === "confirming"}
              variant="outline"
              // @ts-ignore
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
